import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { ContactWithRelations } from '@/types/contact.types';
import {
  fetchContacts,
  fetchContactById,
  createContact,
  updateContact,
  deleteContactSafely,
  mergeContacts,
} from '@/services';
import { CACHE_TIMES, invalidateRelatedQueries } from '@/lib/react-query';

export function useContacts() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { session } = useAuth();

  // Fetch all contacts
  const contactsQuery = useQuery({
    queryKey: ['contacts'],
    queryFn: fetchContacts,
    ...CACHE_TIMES.DYNAMIC, // Contacts change frequently
    enabled: !!session,
  });

  // Create contact mutation
  const createContactMutation = useMutation({
    mutationFn: createContact,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
      toast({
        title: 'Contact created',
        description: 'The contact has been created successfully.',
      });
    },
    onError: (error) => {
      toast({
        title: 'Failed to create contact',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // Update contact mutation
  const updateContactMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => updateContact(id, data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
      queryClient.invalidateQueries({ queryKey: ['contacts', data.id] });
      toast({
        title: 'Contact updated',
        description: 'The contact has been updated successfully.',
      });
    },
    onError: (error) => {
      toast({
        title: 'Failed to update contact',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // Delete contact mutation with dependency checking
  const deleteContactMutation = useMutation({
    mutationFn: deleteContactSafely,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
      toast({
        title: 'Contact deleted',
        description: data.message || 'The contact has been deleted successfully.',
      });
    },
    onError: (error) => {
      toast({
        title: 'Failed to delete contact',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // Merge contacts mutation
  const mergeContactsMutation = useMutation({
    mutationFn: ({ sourceId, targetId }: { sourceId: string; targetId: string }) =>
      mergeContacts(sourceId, targetId),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
      // Also invalidate organization queries since contacts might be associated with orgs
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
      toast({
        title: 'Contacts merged',
        description: data.message || 'The contacts have been successfully merged.',
      });
    },
    onError: (error) => {
      toast({
        title: 'Failed to merge contacts',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  return {
    contacts: contactsQuery.data || [],
    isLoading: contactsQuery.isLoading,
    error: contactsQuery.error,
    createContact: createContactMutation.mutate,
    updateContact: updateContactMutation.mutate,
    deleteContact: deleteContactMutation.mutate,
    mergeContacts: mergeContactsMutation.mutate,
  };
}

// Separate hook for individual contact
export function useContact(id: string) {
  const { session } = useAuth();
  
  return useQuery({
    queryKey: ['contacts', id],
    queryFn: () => fetchContactById(id),
    enabled: !!session && !!id,
    ...CACHE_TIMES.DYNAMIC,
  });
}
